import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { Select } from "antd";
import Spinner from "../../../components/icons/Spinner";
import { useCubeQuery } from "../../../utils/hooks/useCubeQuery";
import DateSelect from "../../../components/buttons/DateSelect";
import { useDateRange, useDateRangeDebounce } from "../../../utils/date-utils";
import Table from "../../../components/lists/Table/Table";
import useCubeClient from "../../../utils/cubejs-client";

// Custom hook for debouncing values (keeping for non-date values)
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AccountReportingWebsites = () => {
  const [types, setTypes] = useState(["STANDARD", "FILLER", "IN_HOUSE", "MARKETPLACE_NATIVE_DISPLAY", "IN_HOUSE_NATIVE_DISPLAY"]);
  const [websites, setWebsites] = useState([]);
  const [uniquewebsites, setUnique] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const cubejsApi = useCubeClient();

  // Using the enhanced useDateRange hook with built-in debouncing
  const {
    dateRange,
    handleDateChange: handleDate,
    debouncedDateRange,
    formattedDateRange
  } = useDateRange(
    [dayjs().add(-730, "d"), dayjs()], // Default to last 2 years
    300 // 300ms debounce
  );

  // Debounce other filter changes
  const debouncedTypes = useDebounce(types, 300);
  const debouncedWebsites = useDebounce(websites, 300);

  const typeOptions = [
    { label: "In House", value: "IN_HOUSE" },
    { label: "In House Display", value: "IN_HOUSE_NATIVE_DISPLAY" },
    { label: "Filler", value: "FILLER" },
    { label: "Marketplace", value: "STANDARD" },
    { label: "Marketplace Display", value: "MARKETPLACE_NATIVE_DISPLAY" },
  ];

  const transformCampaignType = (type) => {
    if (type === 'STANDARD') return 'Marketplace';
    return type.charAt(0) + type.slice(1).toLowerCase().replace('_', ' ');
  };

  const columns = [
    {
      title: "Website Name",
      dataIndex: "aggregateTdLevel.websiteName",
      key: "aggregateTdLevel.websiteName",
      sorter: (a, b) => {
        if (a["aggregateTdLevel.websiteName"] && b["aggregateTdLevel.websiteName"])
          return a["aggregateTdLevel.websiteName"].localeCompare(b["aggregateTdLevel.websiteName"]);
        return a["aggregateTdLevel.websiteName"] ? -1 : 1;
      },
    },
    {
      title: "Campaign Type",
      dataIndex: "aggregateTdLevel.campaignType",
      key: "aggregateTdLevel.campaignType",
      render: (text) => transformCampaignType(text),
    },
    {
      title: "eCPM",
      dataIndex: "aggregateTdLevel.eCpm",
      key: "aggregateTdLevel.eCpm",
      sorter: (a, b) => {
        const ecpmA =
          a["aggregateTdLevel.tdImpressions"] > 0
            ? a["aggregateTdLevel.publisherRevenue"] /
            (a["aggregateTdLevel.tdImpressions"] / 1000)
            : 0;
        const ecpmB =
          b["aggregateTdLevel.tdImpressions"] > 0
            ? b["aggregateTdLevel.publisherRevenue"] /
            (b["aggregateTdLevel.tdImpressions"] / 1000)
            : 0;
        return ecpmA - ecpmB;
      },
      render: (_, record) => {
        const tdImpressions = record["aggregateTdLevel.tdImpressions"];
        const publisherRevenue = record["aggregateTdLevel.publisherRevenue"];

        if (tdImpressions > 0) {
          const calculatedEcpm = publisherRevenue / (tdImpressions / 1000);
          return calculatedEcpm.toFixed(2).toLocaleString(); // Add thousands separator
        } else {
          return "-";
        }
      },
    },
    {
      title: "Revenue",
      dataIndex: "aggregateTdLevel.publisherRevenue",
      key: "aggregateTdLevel.publisherRevenue",
      sorter: (a, b) =>
        a["aggregateTdLevel.publisherRevenue"] - b["aggregateTdLevel.publisherRevenue"],
      render: (value) => {
        const numberValue = parseFloat(value); // Try converting the value to a number
        // Use toLocaleString on the number, specifying minimum and maximum fraction digits for two decimals
        return !isNaN(numberValue)
          ? numberValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : "0.00"; // Format with thousands separator
      },
    },

    {
      title: "Reads",
      dataIndex: "aggregateTdLevel.reads",
      key: "aggregateTdLevel.reads",
      sorter: (a, b) => a["aggregateTdLevel.reads"] - b["aggregateTdLevel.reads"],
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Clicks",
      dataIndex: "aggregateTdLevel.tdClicks",
      key: "aggregateTdLevel.tdClicks",
      sorter: (a, b) => a["aggregateTdLevel.tdClicks"] - b["aggregateTdLevel.tdClicks"],
      render: (value) => parseInt(value || 0).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Impressions",
      dataIndex: "aggregateTdLevel.tdImpressions",
      key: "aggregateTdLevel.tdImpressions",
      sorter: (a, b) =>
        a["aggregateTdLevel.tdImpressions"] - b["aggregateTdLevel.tdImpressions"],
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
  ];

  const totColumns = [
    {
      title: "Campaign Type",
      dataIndex: "aggregateTdLevel.campaignType",
      key: "aggregateTdLevel.campaignType",
      render: (text) => transformCampaignType(text),
    },
    {
      title: "eCPM",
      dataIndex: "aggregateTdLevel.eCpm",
      key: "aggregateTdLevel.eCpm",
      sorter: (a, b) => {
        const ecpmA =
          a["aggregateTdLevel.tdImpressions"] > 0
            ? a["aggregateTdLevel.publisherRevenue"] /
            (a["aggregateTdLevel.tdImpressions"] / 1000)
            : 0;
        const ecpmB =
          b["aggregateTdLevel.tdImpressions"] > 0
            ? b["aggregateTdLevel.publisherRevenue"] /
            (b["aggregateTdLevel.tdImpressions"] / 1000)
            : 0;
        return ecpmA - ecpmB;
      },
      render: (_, record) => {
        const tdImpressions = record["aggregateTdLevel.tdImpressions"];
        const publisherRevenue = record["aggregateTdLevel.publisherRevenue"];

        if (tdImpressions > 0) {
          const calculatedEcpm = publisherRevenue / (tdImpressions / 1000);
          return calculatedEcpm.toFixed(2).toLocaleString(); // Add thousands separator
        } else {
          return "-";
        }
      },
    },
    {
      title: "Revenue",
      dataIndex: "aggregateTdLevel.publisherRevenue",
      key: "aggregateTdLevel.publisherRevenue",
      sorter: (a, b) =>
        a["aggregateTdLevel.publisherRevenue"] - b["aggregateTdLevel.publisherRevenue"],
      render: (value) => {
        const numberValue = parseFloat(value); // Try converting the value to a number
        // Use toLocaleString on the number, specifying minimum and maximum fraction digits for two decimals
        return !isNaN(numberValue)
          ? numberValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : "0.00"; // Format with thousands separator
      },
    },

    {
      title: "Reads",
      dataIndex: "aggregateTdLevel.reads",
      key: "aggregateTdLevel.reads",
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Clicks",
      dataIndex: "aggregateTdLevel.tdClicks",
      key: "aggregateTdLevel.tdClicks",
      render: (value) => parseInt(value || 0).toLocaleString(), // Format with thousands separator
    },
    {
      title: "Impressions",
      dataIndex: "aggregateTdLevel.tdImpressions",
      key: "aggregateTdLevel.tdImpressions",
      render: (value) => parseInt(value).toLocaleString(), // Format with thousands separator
    },
  ];

  // Build query filters with memoization to prevent unnecessary recalculations
  const queryFilters = useMemo(() => [
    {
      member: "aggregateTdLevel.websiteId",
      operator: "set",
    },
    {
      member: "aggregateTdLevel.publisherId",
      operator: "set",
    },
    ...(debouncedTypes.length > 0
      ? [
        {
          member: "aggregateTdLevel.campaignType",
          operator: "equals",
          values: debouncedTypes,
        },
      ]
      : []),
    ...(debouncedWebsites.length > 0
      ? [
        {
          member: "aggregateTdLevel.websiteId",
          operator: "equals",
          values: debouncedWebsites,
        },
      ]
      : []),
  ], [debouncedTypes, debouncedWebsites]);

  // Time dimensions with formatted date range (from our hook)
  const timeDimensions = useMemo(() => [
    {
      dimension: "aggregateTdLevel.date",
      dateRange: formattedDateRange,
    },
  ], [formattedDateRange]);

  const {
    resultSet: websiteData,
    error: websiteError,
    isLoading: websiteLoading,
  } = useCubeQuery(
    {
      dimensions: [
        "aggregateTdLevel.websiteName",
        "aggregateTdLevel.websiteId",
        "aggregateTdLevel.campaignType",
      ],
      measures: [
        "aggregateTdLevel.eCpm",
        "aggregateTdLevel.reads",
        "aggregateTdLevel.publisherRevenue",
        "aggregateTdLevel.tdImpressions",
        "aggregateTdLevel.tdClicks",
      ],
      timeDimensions,
      filters: queryFilters,
    },
    { cubejsApi }
  );

  const {
    resultSet: totData,
    error: totError,
    isLoading: totLoading,
  } = useCubeQuery(
    {
      dimensions: ["aggregateTdLevel.campaignType"],
      measures: [
        "aggregateTdLevel.eCpm",
        "aggregateTdLevel.reads",
        "aggregateTdLevel.publisherRevenue",
        "aggregateTdLevel.tdImpressions",
        "aggregateTdLevel.tdClicks",
      ],
      timeDimensions,
      filters: queryFilters,
    },
    { cubejsApi }
  );

  // Memoize data processing - defined before any conditional returns
  const tableData = useMemo(() =>
      websiteData?.loadResponses[0]?.data || [],
    [websiteData]
  );

  const totTable = useMemo(() =>
      totData?.loadResponses[0]?.data || [],
    [totData]
  );

  // Memoize calculation function
  const calculateTotals = useCallback((data) => {
    if (!data) return {
      totalEcpm: "-",
      totalRevenue: "0.00",
      totalReads: "0",
      totalClicks: "0",
      totalImps: "0",
    };

    const totalRevenue = data.reduce(
      (sum, record) => sum + (parseFloat(record["aggregateTdLevel.publisherRevenue"]) || 0),
      0
    );
    const totalReads = data.reduce(
      (sum, record) => sum + (parseFloat(record["aggregateTdLevel.reads"]) || 0),
      0
    );
    const totalClicks = data.reduce(
      (sum, record) => sum + (parseFloat(record["aggregateTdLevel.tdClicks"]) || 0),
      0
    );
    const totalImps = data.reduce(
      (sum, record) => sum + (parseFloat(record["aggregateTdLevel.tdImpressions"]) || 0),
      0
    );
    const totalEcpm = totalRevenue > 0 ? totalRevenue / (totalImps / 1000) : "-";
    return {
      totalEcpm: typeof totalEcpm === "string" ? "-" : totalEcpm.toFixed(2).toLocaleString(),
      totalRevenue: totalRevenue.toFixed(2).toLocaleString(),
      totalReads: totalReads.toLocaleString(),
      totalClicks: totalClicks.toLocaleString(),
      totalImps: totalImps.toLocaleString(),
    };
  }, []);

  // Memoize calculated totals
  const totalValues = useMemo(() =>
      calculateTotals(totTable),
    [totTable, calculateTotals]
  );

  // Memoize handlers for Select onChange - defined before any conditional returns
  const handleTypeChange = useCallback((newTypes) => setTypes(newTypes), []);
  const handleWebsiteChange = useCallback((newWebsites) => setWebsites(newWebsites), []);

  // Track loading state changes
  useEffect(() => {
    if (websiteLoading || totLoading) {
      setIsRefreshing(true);
    } else {
      setIsRefreshing(false);
    }
  }, [websiteLoading, totLoading]);

  // Process website data for dropdown - only on initial load
  useEffect(() => {
    if (!websiteLoading && websiteData && !websiteError && !isInitialized) {
      const uniqueWebsiteIds = new Set();
      const uniqueWebsites = websiteData.loadResponses[0].data
        .map((item) => ({
          label: item["aggregateTdLevel.websiteName"],
          value: item["aggregateTdLevel.websiteId"],
          key: item["aggregateTdLevel.websiteId"],
        }))
        .filter((website) => {
          if (!uniqueWebsiteIds.has(website.value)) {
            uniqueWebsiteIds.add(website.value);
            return true;
          }
          return false;
        });

      const newWebsites = uniqueWebsites.map((website) => website.value);
      setWebsites(newWebsites);
      setUnique(uniqueWebsites);
      setIsInitialized(true);
    }
  }, [websiteLoading, websiteData, websiteError, isInitialized]);

  // Update dropdown options whenever data changes (without modifying selected websites)
  useEffect(() => {
    if (!websiteLoading && websiteData && !websiteError && isInitialized) {
      const uniqueWebsiteIds = new Set();
      const uniqueWebsites = websiteData.loadResponses[0].data
        .map((item) => ({
          label: item["aggregateTdLevel.websiteName"],
          value: item["aggregateTdLevel.websiteId"],
          key: item["aggregateTdLevel.websiteId"],
        }))
        .filter((website) => {
          if (!uniqueWebsiteIds.has(website.value)) {
            uniqueWebsiteIds.add(website.value);
            return true;
          }
          return false;
        });

      // Only update the dropdown options, not the selected values
      setUnique(uniqueWebsites);
    }
  }, [websiteLoading, websiteData, websiteError, isInitialized]);

  // Handle error states
  if (websiteError) return <div>Error loading data</div>;
  if (websiteLoading || !websiteData || !totData) return <Spinner />;

  return (
    <div className="pb-4 bg-white rounded-lg shadow-lg py-2">
      {isRefreshing && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <Spinner />
        </div>
      )}
      <div className="px-2 sm:px-6 lg:px-4 py-4">
        <div className="p-2">
          <h1 className="text-lg leading-6 font-medium text-gray-900 pb-2">Revenue</h1>
          <div className="sm:flex sm:items-center justify-between">
            <div className="sm:w-1/2">
              <DateSelect dateRange={dateRange} handleDate={handleDate} />
            </div>
            <div className="sm:w-1/2 flex justify-center">
              <Select
                className="w-full sm:w-auto"
                mode="multiple"
                options={typeOptions}
                value={types}
                onChange={handleTypeChange}
              />
            </div>
            <div className="sm:w-1/2 flex justify-end">
              <Select
                className="w-full sm:w-auto"
                mode="multiple"
                options={uniquewebsites}
                value={websites}
                onChange={handleWebsiteChange}
              />
            </div>
          </div>
        </div>

        <Table
          columns={totColumns}
          data={totTable}
          searchable={false}
          className="mb-4"
          footer={() => (
            <div style={{ fontWeight: "bold", textAlign: "right" }}>
              Total Revenue: {totalValues.totalRevenue} | Total Reads: {totalValues.totalReads} | Total Clicks: {totalValues.totalClicks} | Total
              Impressions: {totalValues.totalImps}
            </div>
          )}
        />

        <Table columns={columns} data={tableData} searchable={false} />
      </div>
    </div>
  );
};

export default AccountReportingWebsites;
